import { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import axios from '../../utils/axios';
import { useSnackbar } from '../snackbar';

import './ModalAvaliacao.css';

// ----------------------------------------------------------------------

const STAR_LABELS: { [index: number]: string } = {
    0: '',
    1: 'Precisa melhorar',
    2: 'Razoável',
    3: 'Bom',
    4: 'Muito bom',
    5: 'Excelente',
};

const OPCOES_OUVIDO = [
    { value: 'muito', label: 'Sim, bastante' },
    { value: 'parcial', label: 'Em partes' },
    { value: 'pouco', label: 'Não muito' },
];

const OPCOES_DEPOIS = [
    { value: 'muito_melhor', label: 'Muito melhor' },
    { value: 'pouco_melhor', label: 'Um pouco melhor' },
    { value: 'igual', label: 'Igual' },
    { value: 'pior', label: 'Pior' },
];

const OPCOES_NOVAMENTE = [
    { value: 'sim', label: 'Sim' },
    { value: 'talvez', label: 'Não tenho certeza' },
    { value: 'nao', label: 'Não' },
];

const StarIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

type props = {
    AvaliarOpen: any;
    setAvaliarOpen: any;
    dadosAvaliar: any;
    tipo: any;
};

export default function ModalAvaliacao({ AvaliarOpen, setAvaliarOpen, dadosAvaliar, tipo }: props) {
    const { enqueueSnackbar } = useSnackbar();

    const [value, setValue] = useState<number>(0);
    const [hover, setHover] = useState<number>(0);
    const [texto, settexto] = useState<string>(dadosAvaliar?.texto || '');
    const [ouvido, setOuvido] = useState<string>('');
    const [depois, setDepois] = useState<string>('');
    const [novamente, setNovamente] = useState<string>('');
    const [enviando, setEnviando] = useState<boolean>(false);

    // carrega a avaliação existente quando o modal é aberto em modo edição
    const getmessage = useCallback(async () => {
        try {
            const response = await axios.post(`/areadoheroi/avaliacao`, dadosAvaliar);

            const { avalia } = response.data;

            setValue(Number(avalia?.estrelas) || 0);
            settexto(avalia?.texto || '');
            setOuvido(avalia?.se_sentiu_ouvido || '');
            setDepois(avalia?.como_se_sente || '');
            setNovamente(avalia?.conversaria_novamente || '');
        } catch (error) {
            console.error(error);
        }
    }, [dadosAvaliar]);

    useEffect(() => {
        if (tipo === 'editar') {
            getmessage();
        }
    }, [getmessage, tipo]);

    const handleClose = () => {
        setAvaliarOpen(false);
    };

    const avaliar = async () => {
        if (!value) {
            enqueueSnackbar('Escolha de 1 a 5 estrelas para enviar sua avaliação.', { variant: 'warning' });
            return;
        }

        const id = tipo === 'editar' ? dadosAvaliar.IDAtendimento : dadosAvaliar.id;

        setEnviando(true);

        try {
            const response = await axios.post(`/areadoheroi/avaliar`, {
                id,
                IDAtendente: dadosAvaliar.IDAtendente,
                estrelas: value,
                texto,
                se_sentiu_ouvido: ouvido,
                como_se_sente: depois,
                conversaria_novamente: novamente,
            });

            enqueueSnackbar(`${response.data.message}`, { variant: 'success' });
            setEnviando(false);
            handleClose();
        } catch (error) {
            enqueueSnackbar(`${error.message}`, { variant: 'error' });
            setEnviando(false);
            handleClose();
        }
    };

    const estrelaAtiva = hover || value;

    return (
        <Dialog
            open={AvaliarOpen}
            onClose={handleClose}
            aria-labelledby="eyheModalTitle"
            className="eyhe-modal-root"
            maxWidth={false}
            PaperProps={{ className: 'eyhe-modal-paper' }}
        >
            <button type="button" className="eyhe-close" aria-label="Fechar" onClick={handleClose}>
                &times;
            </button>

            <div className="eyhe-header">
                <div className="eyhe-art" aria-hidden="true">
                    <svg width="64" height="64" viewBox="0 0 168 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M20 78c-2-16 8-30 24-32 8-1 14 3 17 9l6 13-14 22c-9 6-20 4-27-3-5-3-6-6-6-9z"
                            fill="#DCE8FF"
                        />
                        <path
                            d="M148 78c2-16-8-30-24-32-8-1-14 3-17 9l-6 13 14 22c9 6 20 4 27-3 5-3 6-6 6-9z"
                            fill="#DCE8FF"
                        />
                        <g className="eyhe-pulse">
                            <path
                                d="M84 46c-9-13-29-9-29 6 0 15 20 26 29 33 9-7 29-18 29-33 0-15-20-19-29-6z"
                                fill="#FF8552"
                            />
                        </g>
                        <path
                            d="M32 34l2.4 5.6L40 42l-5.6 2.4L32 50l-2.4-5.6L24 42l5.6-2.4z"
                            fill="#FF8552"
                            opacity="0.55"
                        />
                        <path
                            d="M140 30l1.8 4.2L146 36l-4.2 1.8L140 42l-1.8-4.2L134 36l4.2-1.8z"
                            fill="#2F6FED"
                            opacity="0.5"
                        />
                    </svg>
                </div>

                <span className="eyhe-badge">
                    <span className="eyhe-badge-dot" aria-hidden="true" />
                    Antes de você ir
                </span>

                <Typography variant="h6" className="eyhe-title" id="eyheModalTitle">
                    Como foi conversar com seu Anjo?
                </Typography>
                <Typography className="eyhe-subtitle">
                    Sua opinião nos ajuda a manter o EYhe um lugar seguro e acolhedor para você.
                </Typography>
            </div>

            <DialogContent className="eyhe-body">
                {/* Estrelas */}
                <div className="eyhe-section">
                    <Typography component="p" className="eyhe-q-label">
                        Como você avalia essa conversa?
                    </Typography>

                    <div className="eyhe-stars" role="radiogroup" aria-label="Nota da conversa">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                role="radio"
                                aria-checked={value === n}
                                aria-label={`${n} ${n === 1 ? 'estrela' : 'estrelas'}`}
                                className={`eyhe-star ${n <= estrelaAtiva ? 'is-filled' : ''}`}
                                onClick={() => setValue(n)}
                                onMouseEnter={() => setHover(n)}
                                onMouseLeave={() => setHover(0)}
                                onFocus={() => setHover(n)}
                                onBlur={() => setHover(0)}
                            >
                                <StarIcon />
                            </button>
                        ))}
                    </div>

                    <Typography component="p" className="eyhe-stars-caption">
                        {STAR_LABELS[estrelaAtiva] || ' '}
                    </Typography>
                </div>

                <hr className="eyhe-divider" />

                {/* Se sentiu ouvido */}
                <div className="eyhe-section">
                    <Typography component="p" className="eyhe-q-label">
                        Você se sentiu ouvido(a) e acolhido(a)?
                    </Typography>
                    <div className="eyhe-choices" role="radiogroup" aria-label="Você se sentiu ouvido(a) e acolhido(a)?">
                        {OPCOES_OUVIDO.map((op) => (
                            <button
                                key={op.value}
                                type="button"
                                role="radio"
                                aria-checked={ouvido === op.value}
                                className={`eyhe-choice ${ouvido === op.value ? 'is-checked' : ''}`}
                                onClick={() => setOuvido(op.value)}
                            >
                                {op.label}
                            </button>
                        ))}
                    </div>
                </div>

                <hr className="eyhe-divider" />

                {/* Como se sente depois */}
                <div className="eyhe-section">
                    <Typography component="p" className="eyhe-q-label">
                        Comparado a antes da conversa, como você se sente agora?
                    </Typography>
                    <div className="eyhe-choices" role="radiogroup" aria-label="Comparado a antes da conversa, como você se sente agora?">
                        {OPCOES_DEPOIS.map((op) => (
                            <button
                                key={op.value}
                                type="button"
                                role="radio"
                                aria-checked={depois === op.value}
                                className={`eyhe-choice ${depois === op.value ? 'is-checked' : ''}`}
                                onClick={() => setDepois(op.value)}
                            >
                                {op.label}
                            </button>
                        ))}
                    </div>
                </div>

                <hr className="eyhe-divider" />

                {/* Conversaria novamente */}
                <div className="eyhe-section">
                    <Typography component="p" className="eyhe-q-label">
                        Você conversaria com esse Anjo novamente?
                    </Typography>
                    <div className="eyhe-choices" role="radiogroup" aria-label="Você conversaria com esse Anjo novamente?">
                        {OPCOES_NOVAMENTE.map((op) => (
                            <button
                                key={op.value}
                                type="button"
                                role="radio"
                                aria-checked={novamente === op.value}
                                className={`eyhe-choice blue ${novamente === op.value ? 'is-checked' : ''}`}
                                onClick={() => setNovamente(op.value)}
                            >
                                {op.label}
                            </button>
                        ))}
                    </div>
                </div>

                <hr className="eyhe-divider" />

                {/* Mensagem livre */}
                <div className="eyhe-section">
                    <Typography component="p" className="eyhe-q-label">
                        Quer deixar uma mensagem para o seu Anjo?
                        <span className="eyhe-q-optional">(opcional)</span>
                    </Typography>
                    <textarea
                        className="eyhe-textarea"
                        aria-label="Mensagem para o seu Anjo"
                        placeholder="Escreva aqui, se quiser."
                        value={texto}
                        onChange={(event) => settexto(event.target.value)}
                    />
                </div>
            </DialogContent>

            <DialogActions className="eyhe-footer">
                <button type="button" className="eyhe-skip" onClick={handleClose}>
                    Pular por agora
                </button>
                <button type="button" className="eyhe-submit" onClick={avaliar} disabled={enviando}>
                    {enviando ? 'Enviando...' : 'Enviar avaliação'}
                </button>
            </DialogActions>
        </Dialog>
    );
}
